import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { ItemPicker } from './ItemPicker';
import { CustomerSelect } from './CustomerSelect';
import { useAuth } from '../../store/auth';
import { useBranches } from '../../hooks/useBranches';
import { fine, money, today, weight } from '../../utils/format';
import { ErrorNotice, PageHeader } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { useSettings } from '../../hooks/useSettings';
import { usePrintHeader } from '../../hooks/usePrintHeader';
import { SALE_COLUMNS, chosenColumns } from '../../utils/documentColumns';

interface ScannedItem {
  id: string;
  item_code: string;
  product_name: string;
  /** This piece's own photo, or its product's if it was never photographed. */
  image_data_uri?: string | null;
  location_name?: string | null;
  purity_code: string;
  gross_wt: string;
  stone_wt?: string;
  net_wt: string;
  fine_wt: string;
  branch_id: string;
}

interface Quote {
  netWt: string;
  fineWt: string;
  wastageWt: string;
  metalValue: string;
  makingAmount: string;
  stoneAmount: string;
  taxableValue: string;
  taxTotal: string;
  lineTotal: string;
  /** The rate this piece was priced at, per gram at its purity. */
  rateValue: string;
  /** Wastage as a % of net weight, and making per net gram, as priced. */
  wastagePctOfNet?: string;
  makingPerGramNet?: string;
  figuresFrom?: { wastage: Source; making: Source; tax: Source };
  bought?: {
    ratePerGram?: string;
    wastage?: { mode: string; value: string };
    making?: { mode: string; value: string };
    stoneAmount?: string;
    taxPct?: string;
  };
  /** What the piece cost the shop. Only sent to roles allowed to see cost. */
  acquisitionCost?: string;
  grossProfit?: string;
  /** That cost per gram of net weight, and the rate at which this sale breaks even. */
  costPerGram?: string;
  breakEvenRate?: string;
  trace: { step: string; expression: string; result: string }[];
}

type Source = 'COUNTER' | 'PRODUCT' | 'PURCHASE' | 'NONE';

/** A few words under a figure saying where it came from. */
const SOURCE_NOTE: Record<Source, string> = {
  COUNTER: 'changed',
  PRODUCT: 'product rule',
  PURCHASE: 'as bought',
  NONE: 'none set',
};

/** The supplier's terms in a jeweller's own shorthand: "5%", "₹18/g", "₹1,500". */
const termText = (t: { mode: string; value: string } | undefined): string => {
  if (!t) return '';
  const v = String(Number(Number(t.value).toFixed(2)));
  return t.mode === 'PCT' ? `${v}%` : t.mode === 'PER_GRAM' ? `₹${v}/g` : `₹${v}`;
};

interface Line {
  item: ScannedItem;
  quote: Quote;
  /** A manual reduction on this line's total, in rupees. */
  discount: string;
  /** The rate/g the cashier typed, if they overrode today's rate. */
  rateOverride: string;
  /** Wastage agreed for this piece, as a percentage of net weight. */
  wastagePct: string;
  /** Making agreed for this piece, in rupees per gram of net weight. */
  makingPerGram: string;
  /** The stones' value on this piece. */
  stoneAmount: string;
  /**
   * Which boxes the cashier has changed. Every box starts filled with what the
   * piece is priced at; only a changed one is sent as an override, so an
   * untouched making rule that is a percentage still follows a new rate.
   */
  edited: { rate?: boolean; wastage?: boolean; making?: boolean; stone?: boolean };
}

/** "8.00" → "8", "350.50" → "350.5": the figure as somebody would type it. */
const plain = (v: string | undefined, dp = 2): string => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? String(Number(n.toFixed(dp))) : '0';
};

/** Fill every box the cashier has not changed from the latest quote. */
const filled = (
  line: Omit<Line, 'rateOverride' | 'wastagePct' | 'makingPerGram' | 'stoneAmount'> &
    Partial<Pick<Line, 'rateOverride' | 'wastagePct' | 'makingPerGram' | 'stoneAmount'>>,
): Line => {
  const q = line.quote;
  const e = line.edited;
  return {
    ...line,
    rateOverride: e.rate ? (line.rateOverride ?? '') : plain(q.rateValue),
    wastagePct: e.wastage ? (line.wastagePct ?? '') : plain(q.wastagePctOfNet),
    makingPerGram: e.making ? (line.makingPerGram ?? '') : plain(q.makingPerGramNet),
    stoneAmount: e.stone ? (line.stoneAmount ?? '') : plain(q.stoneAmount),
  };
};

/** The overrides to send for a line: only the boxes that were changed. */
// A box changed and then cleared prices at the product's own figure, which
// shows faintly in it, rather than refilling under the cashier's cursor.
const overridesOf = (l: Line): Record<string, unknown> => ({
  ...(l.edited.rate && Number(l.rateOverride) > 0 ? { rateOverride: Number(l.rateOverride).toFixed(2) } : {}),
  ...(l.edited.wastage && l.wastagePct !== ''
    ? { wastageOverride: { mode: 'PCT_OF_NET', value: Number(l.wastagePct || 0).toFixed(2) } }
    : {}),
  ...(l.edited.making && l.makingPerGram !== ''
    ? { makingOverride: { mode: 'PER_GRAM_NET', value: Number(l.makingPerGram || 0).toFixed(2) } }
    : {}),
  ...(l.edited.stone && l.stoneAmount !== '' ? { stoneAmountOverride: Number(l.stoneAmount || 0).toFixed(2) } : {}),
});

interface PostedSale {
  saleId: string;
  docNo: string;
  grandTotal: string;
  balance: string;
}

/**
 * The till. Scan, see the price the server would charge, take the money.
 *
 * The price shown is never computed here — it comes from /pricing/quote, which
 * runs the same engine that will post the invoice. What the customer is told
 * and what the books record cannot drift apart.
 */
/** Columns holding a figure, so heading and value line up. */
const NUMERIC_COLS = new Set([
  'grossWt',
  'stoneWt',
  'netWt',
  'fineWt',
  'rate',
  'metal',
  'wastage',
  'making',
  'stoneAmount',
  'otherCharges',
  'discount',
  'tax',
  'cost',
  'profit',
  'total',
]);

export function PosPage() {
  const session = useAuth((s) => s.session);
  const can = useAuth((s) => s.can);
  const onEnter = useKeyboardForm();
  // Whether this person may see cost and margin at all. A cashier prices and
  // sells; only a role that carries sales.view_profit sees what the shop makes.
  const navigate = useNavigate();
  const { setting } = useSettings();
  const { header: shop } = usePrintHeader();

  // An unregistered shop cannot charge GST at all, so the switch starts off
  // for them and on for everybody else.
  const registered = !!shop.companyGstin;
  useEffect(() => {
    setWithGst(registered);
  }, [registered]);
  const showProfit = can('sales.view_profit');

  // Which figures this shop bills with. Everything is worked out either way;
  // the choice decides what a cashier sees and can change.
  const cols = chosenColumns(
    setting('documents.sale_columns', SALE_COLUMNS.map((c) => c.key).join(',')),
    SALE_COLUMNS,
  ).filter((c) => showProfit || (c.key !== 'cost' && c.key !== 'profit'));
  const { branches, defaultBranchId } = useBranches();
  const toast = useToast();
  // The session's branch list can be empty for an owner who works across all
  // branches; the real list from the server is the reliable source. A sale
  // still needs one specific branch, so the first is chosen and can be changed.
  const [branchId, setBranchId] = useState('');
  const activeBranch = branchId || defaultBranchId || session?.branchIds[0] || '';
  const [barcode, setBarcode] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [customer, setCustomer] = useState<{ id: string; name: string } | null>(null);
  const [payMode, setPayMode] = useState('CASH');
  // BILL posts a real invoice; ESTIMATE saves a quote that moves no stock.
  const [docMode, setDocMode] = useState<'BILL' | 'ESTIMATE'>('BILL');
  // A shop with no GSTIN is not registered, so its bills carry no GST at all.
  // A registered shop still sells over the counter without a tax invoice, so
  // it is a switch rather than a setting.
  const [withGst, setWithGst] = useState(true);
  /** Blank means "whatever each product's tax group says" — usually 3%. */
  const [gstPct, setGstPct] = useState('');
  /** The GST rate as the server will accept it — blank while mid-typing. */
  const validGstPct = (v: string): string => (/^\d{1,2}(\.\d{1,2})?$/.test(v) ? v : '');
  const [cash, setCash] = useState('');
  // Money the customer already has with the shop — an advance, or a savings
  // scheme redeemed into one — applied before anything else is taken.
  const [useAdvance, setUseAdvance] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [posted, setPosted] = useState<PostedSale | null>(null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [enlarged, setEnlarged] = useState<ScannedItem | null>(null);
  const scanRef = useRef<HTMLInputElement>(null);

  /**
   * The invoice is rounded to the nearest rupee, and the server refuses a
   * walk-in sale that is left even 32 paise short. So the figure shown to the
   * cashier is the one they will actually be asked for.
   */
  const lineNet = (l: Line): number => Number(l.quote.lineTotal) - Number(l.discount || 0);
  const lineProfit = (l: Line): number | null =>
    l.quote.grossProfit === undefined ? null : Number(l.quote.grossProfit) - Number(l.discount || 0);

  const lineSum = lines.reduce((a, l) => a + lineNet(l), 0);
  const total = Math.round(lineSum);

  const advance = useQuery({
    queryKey: ['customer-advance', customer?.id],
    queryFn: () => api.get<{ balance: string }>(`/customers/${customer!.id}/advance`),
    enabled: !!customer && docMode === 'BILL',
  });
  const onAccount = Number(advance.data?.balance ?? 0);
  const advanceUsed = useAdvance && onAccount > 0 ? Math.min(onAccount, total) : 0;
  const stillToPay = Math.max(0, total - advanceUsed);
  const roundOff = total - lineSum;

  // The bill broken into the parts a customer expects to see: the metal-plus-
  // making before tax, the tax, and any discount given.
  const subTotal = lines.reduce((a, l) => a + Number(l.quote.taxableValue), 0);
  const taxTotal = lines.reduce((a, l) => a + Number(l.quote.taxTotal), 0);
  const discountTotal = lines.reduce((a, l) => a + Number(l.discount || 0), 0);

  // What the whole bill makes, once the discounts are taken off.
  const billProfit = lines.reduce((a, l) => a + (lineProfit(l) ?? 0), 0);
  const anyDiscount = lines.some((l) => Number(l.discount || 0) > 0);

  /** Adds one piece to the bill, whether it was scanned or picked from the list. */
  const addItem = async (item: ScannedItem): Promise<void> => {
    if (lines.some((l) => l.item.id === item.id)) {
      setError(new ApiError({ code: 'DUP', detail: `${item.item_code} is already on this bill.`, status: 409 }));
      return;
    }
    const quote = await api.post<Quote>('/pricing/quote', {
      stockItemId: item.id,
      withoutTax: !withGst,
      ...(withGst && validGstPct(gstPct) ? { taxPctOverride: validGstPct(gstPct) } : {}),
    });
    setLines((current) => [...current, filled({ item, quote, discount: '', edited: {} })]);
  };

  /**
   * Re-price one line at a rate the cashier typed.
   *
   * The whole point of a weight-based till is that the price follows the rate,
   * so an override is not a discount bolted on the end — it is a fresh quote at
   * the new rate, with metal value, making, tax and profit all recalculated by
   * the same engine that priced the original. An empty box restores today's
   * rate.
   */
  const reprice = async (
    itemId: string,
    patch: Partial<Line>,
    // The tax choice is passed in, because when the GST toggle or rate has
    // just changed, its state has not settled yet and reading it from the
    // closure would price against the old value.
    tax: { withGst: boolean; gstPct: string } = { withGst, gstPct },
  ): Promise<void> => {
    // Work out the new line from what is on screen right now, then set state
    // and quote from that same value. Reading it back out of the setLines
    // updater was unreliable — React may run that updater more than once, and
    // in StrictMode does — so the typed figure sometimes never reached the
    // server and the piece re-priced on the product's own rate instead.
    const current = lines.find((l) => l.item.id === itemId);
    if (!current) return;
    const line: Line = { ...current, ...patch };
    setLines((c) => c.map((l) => (l.item.id === itemId ? line : l)));
    try {
      const quote = await api.post<Quote>('/pricing/quote', {
        stockItemId: itemId,
        withoutTax: !tax.withGst,
        ...(tax.withGst && validGstPct(tax.gstPct) ? { taxPctOverride: validGstPct(tax.gstPct) } : {}),
        // Wastage as a share of the net weight, and making per gram of it —
        // the two ways a counter actually settles a piece. Only what was
        // changed is sent; the rest follows the product's own rules.
        ...overridesOf(line),
      });
      // The changed boxes keep what was typed; the others refresh, so a new
      // rate shows the wastage and making it now comes to.
      setLines((c) => c.map((l) => (l.item.id === itemId ? filled({ ...l, quote }) : l)));
    } catch (err) {
      setError(err);
    }
  };

  const scan = async (e: FormEvent) => {
    e.preventDefault();
    if (!barcode.trim()) return;
    setError(null);
    try {
      const item = await api.get<ScannedItem>(
        `/items/scan/${encodeURIComponent(barcode.trim())}${activeBranch ? `?branchId=${activeBranch}` : ''}`,
      );
      await addItem(item);
      setBarcode('');
    } catch (err) {
      setError(err);
    } finally {
      scanRef.current?.focus();
    }
  };

  /**
   * "Sell" pressed on another screen — a bar on the melting page, say — opens
   * the counter with that piece already on the bill: /pos?item=<tag>.
   */
  const [params, setParams] = useSearchParams();
  const preset = params.get('item');
  useEffect(() => {
    if (!preset) return;
    setParams(
      (p) => {
        p.delete('item');
        return p;
      },
      { replace: true },
    );
    void (async () => {
      try {
        const item = await api.get<ScannedItem>(
          `/items/scan/${encodeURIComponent(preset)}${activeBranch ? `?branchId=${activeBranch}` : ''}`,
        );
        await addItem(item);
      } catch (err) {
        setError(err);
      }
    })();
    // Once, for the tag the page was opened with.
  }, [preset]);

  const linePayload = () =>
    lines.map((l) => ({
      stockItemId: l.item.id,
      ...(Number(l.discount || 0) > 0
        ? { discount: { mode: 'AMOUNT', value: l.discount, appliesTo: 'LINE_TOTAL' } }
        : {}),
      ...overridesOf(l),
    }));

  const clear = (): void => {
    setLines([]);
    setCash('');
    setCustomer(null);
  };

  const post = async () => {
    setBusy(true);
    setError(null);
    try {
      if (docMode === 'ESTIMATE') {
        // A quote: same pricing, no money, no stock movement.
        const est = await api.post<{ docNo: string; grandTotal: string }>('/estimates', {
          branchId: activeBranch,
          docDate: today(),
          ...(customer ? { customerId: customer.id } : { walkinName: 'Walk-in' }),
          withoutTax: !withGst,
          ...(withGst && validGstPct(gstPct) ? { taxPctOverride: validGstPct(gstPct) } : {}),
          lines: linePayload(),
        });
        toast(`Estimate ${est.docNo} saved`, `${money(est.grandTotal)} — no stock moved, nothing billed`);
        clear();
        return;
      }

      const sale = await api.post<PostedSale>(
        '/sales',
        {
          branchId: activeBranch,
          docDate: today(),
          ...(customer ? { customerId: customer.id } : { walkinName: 'Walk-in' }),
          withoutTax: !withGst,
          ...(withGst && validGstPct(gstPct) ? { taxPctOverride: validGstPct(gstPct) } : {}),
          lines: linePayload(),
          payments: [
            ...(advanceUsed > 0 ? [{ mode: 'ADVANCE', amount: advanceUsed.toFixed(2) }] : []),
            ...(cash ? [{ mode: payMode, amount: cash }] : []),
          ],
        },
        { 'idempotency-key': crypto.randomUUID() },
      );
      setPosted(sale);
      clear();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  /** A small box the cashier can type a figure into, right-aligned like the column. */
  const editBox = (
    value: string,
    placeholder: string,
    onChange: (v: string) => void,
    title: string,
    // Gold when the cashier has changed it from what the piece is priced at.
    changed = !!value,
    unit?: { before?: string; after?: string },
    note?: { text: string; tone?: 'good' | 'muted' | 'warn' },
  ): JSX.Element => (
    <div className="inline-flex flex-col items-end">
      <label
        className={`flex h-8 items-center rounded-md border bg-surface px-2 focus-within:border-gold-500 focus-within:ring-1 focus-within:ring-gold-300 ${
          changed ? 'border-gold-500 bg-gold-50' : 'border-silver-300'
        }`}
        title={title}
      >
        {unit?.before ? <span className="mr-1 text-xs text-ink-faint">{unit.before}</span> : null}
        <input
          className="num w-16 bg-transparent text-right text-sm outline-none"
          inputMode="decimal"
          autoComplete="off"
          aria-label={title}
          value={value}
          placeholder={placeholder}
          title={title}
          onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))}
        />
        {unit?.after ? <span className="ml-1 text-xs text-ink-faint">{unit.after}</span> : null}
      </label>
      {note ? (
        <span
          className={`mt-0.5 text-2xs ${
            note.tone === 'good' ? 'text-gold-700' : note.tone === 'warn' ? 'text-danger' : 'text-ink-faint'
          }`}
        >
          {note.text}
        </span>
      ) : null}
    </div>
  );

  /**
   * One figure of one line. Everything is already worked out by the pricing
   * engine; three of these can be typed into, and doing so re-prices the whole
   * line rather than adjusting the total afterwards — so the bill always says
   * how it was arrived at.
   */
  const cell = (line: Line, key: string): JSX.Element | string => {
    const q = line.quote;
    switch (key) {
      case 'item':
        return (
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-silver-100 bg-silver-50"
              onClick={() => line.item.image_data_uri && setEnlarged(line.item)}
              title={line.item.image_data_uri ? 'Tap to enlarge' : 'No photo'}
            >
              {line.item.image_data_uri ? (
                <img src={line.item.image_data_uri} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="grid h-full w-full place-items-center text-2xs text-ink-faint">no photo</span>
              )}
            </button>
            <div>
              <span className="font-medium">{line.item.item_code}</span>
              <span className="block text-2xs text-ink-soft">{line.item.product_name}</span>
              {line.item.location_name ? (
                <span className="block text-2xs text-ink-faint">from {line.item.location_name}</span>
              ) : null}
              {showProfit ? (
                <span className="num block text-2xs text-ink-soft">
                  bought at {money(q.acquisitionCost ?? '0')}
                  {q.costPerGram ? ` · ${money(q.costPerGram)}/g net` : ''}
                </span>
              ) : null}
              {/* The supplier's terms, so the counter can see what it is
                  working from without opening the purchase. */}
              {showProfit && q.bought ? (
                <span className="num block text-2xs text-ink-faint">
                  {[
                    q.bought.ratePerGram ? `rate ₹${plain(q.bought.ratePerGram)}/g` : '',
                    q.bought.wastage ? `wastage ${termText(q.bought.wastage)}` : '',
                    q.bought.making ? `making ${termText(q.bought.making)}` : '',
                    q.bought.stoneAmount ? `stones ₹${plain(q.bought.stoneAmount)}` : '',
                    q.bought.taxPct ? `GST ${plain(q.bought.taxPct)}%` : '',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              ) : null}
            </div>
          </div>
        );
      case 'purity':
        return line.item.purity_code;
      case 'grossWt':
        return weight(line.item.gross_wt);
      case 'stoneWt':
        return weight(line.item.stone_wt ?? '0');
      case 'netWt':
        return weight(q.netWt);
      case 'fineWt':
        return fine(q.fineWt);
      case 'rate': {
        const floor = Number(q.breakEvenRate ?? 0);
        const asked = Number(line.rateOverride || q.rateValue);
        const rateChanged = !!line.edited.rate && line.rateOverride !== '';
        return editBox(
          line.rateOverride,
          plain(q.rateValue),
          (v) => void reprice(line.item.id, { rateOverride: v, edited: { ...line.edited, rate: true } }),
          "Today's rate — type another to re-price this piece; clear it to go back",
          rateChanged,
          { before: '₹', after: '/g' },
          // What the piece cost, in the same units as the rate being changed:
          // "how far can I come down" answered without arithmetic.
          showProfit && floor > 0
            ? {
                text: `${asked < floor ? 'below cost · ' : 'cost '}${money(floor.toFixed(0))}/g`,
                tone: asked < floor ? 'warn' : 'muted',
              }
            : { text: rateChanged ? 'changed' : "today's rate", tone: rateChanged ? 'good' : 'muted' },
        );
      }
      case 'metal':
        return money(q.metalValue);
      case 'wastage': {
        const changed = !!line.edited.wastage && line.wastagePct !== '';
        const from = changed ? 'COUNTER' : (q.figuresFrom?.wastage ?? 'NONE');
        const rupees = Number(q.wastageWt) * Number(q.rateValue);
        return editBox(
          line.wastagePct,
          plain(q.wastagePctOfNet),
          (v) => void reprice(line.item.id, { wastagePct: v, edited: { ...line.edited, wastage: true } }),
          'Wastage as a percentage of net weight; clear it to go back',
          changed,
          { after: '%' },
          {
            // The customer argues in grams and rupees, not percentages.
            text: `${rupees > 0 ? `${fine(q.wastageWt)} · ${money(rupees.toFixed(2))} · ` : ''}${SOURCE_NOTE[from]}`,
            tone: from === 'COUNTER' ? 'good' : 'muted',
          },
        );
      }
      case 'making': {
        const changed = !!line.edited.making && line.makingPerGram !== '';
        const from = changed ? 'COUNTER' : (q.figuresFrom?.making ?? 'NONE');
        return editBox(
          line.makingPerGram,
          plain(q.makingPerGramNet),
          (v) => void reprice(line.item.id, { makingPerGram: v, edited: { ...line.edited, making: true } }),
          'Making per gram of net weight; clear it to go back',
          changed,
          { before: '₹', after: '/g' },
          { text: `${money(q.makingAmount)} · ${SOURCE_NOTE[from]}`, tone: from === 'COUNTER' ? 'good' : 'muted' },
        );
      }
      case 'stoneAmount': {
        const changed = !!line.edited.stone && line.stoneAmount !== '';
        return editBox(
          line.stoneAmount,
          plain(q.stoneAmount),
          (v) => void reprice(line.item.id, { stoneAmount: v, edited: { ...line.edited, stone: true } }),
          'The stones’ value on this piece; clear it to go back',
          changed,
          { before: '₹' },
          { text: changed ? 'changed' : 'as tagged', tone: changed ? 'good' : 'muted' },
        );
      }
      case 'otherCharges':
        // Hallmarking and the like are added to the bill, not to a piece's
        // quote, so a line shows none of its own.
        return money('0');
      case 'discount':
        return editBox(
          line.discount,
          '0',
          (v) => setLines((c) => c.map((l) => (l.item.id === line.item.id ? { ...l, discount: v } : l))),
          'Taken off this line',
          !!line.discount,
          { before: '₹' },
        );
      case 'tax': {
        const from = q.figuresFrom?.tax ?? 'NONE';
        return (
          <div className="inline-block text-right">
            <span>{money(q.taxTotal)}</span>
            <span className="block text-2xs text-ink-faint">
              {Number(q.taxTotal) > 0
                ? `${plain(((Number(q.taxTotal) / Number(q.taxableValue || 1)) * 100).toFixed(2))}% · `
                : ''}
              {from === 'NONE'
                ? withGst
                  ? 'no rate set'
                  : 'without GST'
                : from === 'COUNTER'
                  ? 'typed rate'
                  : SOURCE_NOTE[from]}
            </span>
          </div>
        );
      }
      case 'cost':
        return <span className="text-ink-soft">{money(q.acquisitionCost)}</span>;
      case 'profit': {
        const p = lineProfit(line);
        if (p === null) return '';
        return <span className={p < 0 ? 'text-danger' : 'text-success'}>{money(p.toFixed(2))}</span>;
      }
      case 'total':
        return money(lineNet(line).toFixed(2));
      default:
        return '';
    }
  };

  return (
    <>
      <PageHeader
        title="Sell"
        subtitle={
          docMode === 'BILL'
            ? 'Scan a tag to add it to the bill. Rate, making and tax are shown so an offer can be judged on the spot.'
            : 'An estimate: same pricing, but nothing is billed and no stock moves. Convert it to a bill when the customer commits.'
        }
        actions={
          <div className="flex flex-wrap items-center gap-3">
            {docMode === 'BILL' ? (
              <div className="flex items-center gap-2">
                <div className="flex overflow-hidden rounded-md border border-velvet">
                  {([true, false] as const).map((on) => (
                    <button
                      key={String(on)}
                      title={
                        !registered && on
                          ? 'No GSTIN is on record for this shop — add one in Settings so it prints on the bill.'
                          : undefined
                      }
                      className={`px-3 py-1.5 text-sm font-medium ${
                        withGst === on ? 'bg-velvet text-white' : 'bg-surface text-ink-soft hover:bg-gold-50'
                      }`}
                      onClick={() => {
                        setWithGst(on);
                        // Every line is priced again with the choice just made.
                        for (const l of lines) void reprice(l.item.id, {}, { withGst: on, gstPct });
                      }}
                    >
                      {on ? 'With GST' : 'Without GST'}
                    </button>
                  ))}
                </div>

                {withGst && !registered ? (
                  <span className="text-2xs text-warn">No GSTIN on record — add one in Settings.</span>
                ) : null}

                {withGst ? (
                  <label className="flex items-center gap-1 text-2xs text-ink-soft">
                    at
                    <input
                      className="field num w-16 px-2 py-1 text-right text-sm"
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder="3"
                      value={gstPct}
                      title="Leave blank to use the rate set against each product"
                      onChange={(e) => {
                        // Allow only digits and a single dot, at most two
                        // decimals — but keep a half-typed "3." in the box.
                        let v = e.target.value.replace(/[^\d.]/g, '');
                        const dot = v.indexOf('.');
                        if (dot !== -1)
                          v =
                            v.slice(0, dot + 1) +
                            v
                              .slice(dot + 1)
                              .replace(/\./g, '')
                              .slice(0, 2);
                        if (Number(v) > 28) v = '28';
                        setGstPct(v);
                        // Re-price only when the value is one the server takes,
                        // so a mid-keystroke "3." never fails the quote.
                        for (const l of lines) void reprice(l.item.id, {}, { withGst, gstPct: v });
                      }}
                    />
                    %
                  </label>
                ) : null}
              </div>
            ) : null}

            <div className="flex overflow-hidden rounded-md border border-velvet">
              <button
                className={`px-4 py-1.5 text-sm font-medium ${
                  docMode === 'BILL' ? 'bg-velvet text-white' : 'bg-surface text-ink-soft hover:bg-gold-50'
                }`}
                onClick={() => setDocMode('BILL')}
              >
                Bill
              </button>
              <button
                className={`px-4 py-1.5 text-sm font-medium ${
                  docMode === 'ESTIMATE' ? 'bg-velvet text-white' : 'bg-surface text-ink-soft hover:bg-gold-50'
                }`}
                onClick={() => setDocMode('ESTIMATE')}
              >
                Estimate
              </button>
            </div>
          </div>
        }
      />
      <ErrorNotice error={error} />

      {posted ? (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-success/30 bg-success/5 p-3 text-sm">
          <span>
            Bill <span className="num font-medium">{posted.docNo}</span> saved for{' '}
            <span className="num font-medium">{money(posted.grandTotal)}</span>
            {Number(posted.balance) > 0 ? <> — {money(posted.balance)} still owed.</> : '.'}
          </span>
          {/* Saving the bill is only half of it; the customer is standing there
              waiting for a copy. */}
          <button
            className="btn-primary ml-auto py-1.5 text-xs"
            onClick={() => navigate(`/sales/${posted.saleId}/print`)}
          >
            Print / PDF
          </button>
          <button className="btn-ghost py-1.5 text-xs" onClick={() => setPosted(null)}>
            Next bill
          </button>
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form autoComplete="off" onKeyDown={onEnter} onSubmit={(e) => void scan(e)} className="flex gap-2">
          <input
            ref={scanRef}
            className="field w-72"
            placeholder="Scan barcode or type tag no."
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            autoFocus
          />
          <button className="btn-ghost">Add</button>
        </form>
        {/* No scanner? Pick from the shelf. Typing the barcode is faster when
            you have it; this is for when you do not. */}
        <span className="text-2xs text-ink-faint">or</span>
        <button className="btn-ghost" onClick={() => setPicking(true)}>
          Browse stock
        </button>
      </div>

      {enlarged ? (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-rail/60 p-6"
          onClick={() => setEnlarged(null)}
          role="dialog"
          aria-label="Piece photo"
        >
          <div
            className="max-w-lg overflow-hidden rounded-xl bg-surface shadow-pop"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={enlarged.image_data_uri ?? ''} alt="" className="max-h-[70vh] w-full object-contain" />
            <div className="flex items-center justify-between px-4 py-2 text-sm">
              <span>
                <span className="font-medium">{enlarged.item_code}</span>
                <span className="ml-2 text-ink-soft">{enlarged.product_name}</span>
              </span>
              <button className="btn-ghost py-1 text-xs" onClick={() => setEnlarged(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {picking ? (
        <ItemPicker
          branchId={activeBranch}
          alreadyOn={lines.map((l) => l.item.id)}
          onPick={(item) => {
            void addItem(item);
          }}
          onClose={() => setPicking(false)}
        />
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-silver-100 bg-surface shadow-card">
        <table className="w-full">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.key} className={`th ${NUMERIC_COLS.has(c.key) ? 'text-right' : ''}`}>
                  {c.label}
                </th>
              ))}
              <th className="th" />
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td className="td text-center text-ink-soft" colSpan={cols.length + 1}>
                  Nothing on the bill yet. Scan a tag to begin.
                </td>
              </tr>
            ) : (
              lines.map((line) => (
                <tr key={line.item.id}>
                  {cols.map((c) => (
                    <td
                      key={c.key}
                      className={`td ${NUMERIC_COLS.has(c.key) ? 'num text-right' : ''} ${
                        c.key === 'total' ? 'font-medium' : ''
                      }`}
                    >
                      {cell(line, c.key)}
                    </td>
                  ))}
                  <td className="td text-right">
                    <button
                      className="text-2xs text-ink-soft underline hover:text-danger"
                      onClick={() => setLines((c) => c.filter((l) => l.item.id !== line.item.id))}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {lines.length > 0 ? (
        <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_360px]">
          {/* Who and how they are paying. */}
          <div className="space-y-3">
            {branches.length > 1 ? (
              <div>
                <p className="label">Counter</p>
                <div className="flex flex-wrap gap-2">
                  {branches.map((b) => (
                    <button
                      key={b.id}
                      className={`rounded-md border px-3 py-1.5 text-sm ${
                        activeBranch === b.id
                          ? 'border-gold-500 bg-gold-50 font-medium text-gold-700'
                          : 'border-silver-100 text-ink-soft hover:border-silver-400'
                      }`}
                      onClick={() => setBranchId(b.id)}
                    >
                      {b.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div>
              <p className="label">Customer</p>
              <CustomerSelect value={customer} onChange={setCustomer} />
              {docMode === 'BILL' && onAccount > 0 ? (
                <label className="mt-2 flex items-center justify-between gap-2 rounded-md border border-gold-300 bg-gold-50 px-3 py-2 text-sm">
                  <span className="flex items-center gap-2">
                    <input type="checkbox" checked={useAdvance} onChange={(e) => setUseAdvance(e.target.checked)} />
                    Use advance
                    <span className="text-2xs text-ink-soft">(paid ahead, old gold kept, or redeemed savings)</span>
                  </span>
                  <span className="num font-semibold text-gold-700">{money(onAccount.toFixed(2))}</span>
                </label>
              ) : null}
            </div>

            {docMode === 'BILL' ? (
              <>
                <div className="flex gap-2">
                  {['CASH', 'UPI', 'CARD', 'BANK'].map((m) => (
                    <button
                      key={m}
                      className={`rounded-md border px-3 py-1.5 text-sm ${
                        payMode === m
                          ? 'border-gold-500 bg-gold-50 font-medium text-gold-700'
                          : 'border-silver-100 text-ink-soft hover:border-silver-400'
                      }`}
                      onClick={() => setPayMode(m)}
                    >
                      {m === 'CASH' ? 'Cash' : m === 'UPI' ? 'UPI' : m === 'CARD' ? 'Card' : 'Bank'}
                    </button>
                  ))}
                </div>

                <div>
                  <label className="label" htmlFor="cash">
                    {payMode === 'CASH' ? 'Cash taken' : 'Amount received'}
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="cash"
                      className="field num w-48"
                      value={cash}
                      onChange={(e) => setCash(e.target.value.replace(/[^\d.]/g, ''))}
                      placeholder="0.00"
                    />
                    <button className="btn-ghost" onClick={() => setCash(stillToPay.toFixed(2))}>
                      Exact
                    </button>
                  </div>
                  {cash && Number(cash) < stillToPay ? (
                    <p className="num mt-1 text-2xs text-warn">
                      {money((stillToPay - Number(cash)).toFixed(2))} will be left owing
                    </p>
                  ) : cash && Number(cash) > stillToPay ? (
                    <p className="num mt-1 text-2xs text-ink-soft">
                      Change {money((Number(cash) - stillToPay).toFixed(2))}
                    </p>
                  ) : null}
                </div>
              </>
            ) : (
              <p className="rounded-md border border-gold-300 bg-gold-50 px-3 py-2 text-2xs text-ink-soft">
                No money is taken on an estimate. Choose the customer, save it, and convert it to a bill when they buy.
              </p>
            )}
          </div>

          {/* The summary — the one panel a cashier reads before taking money. */}
          <div className="rounded-xl border border-silver-100 bg-surface p-4 shadow-card">
            <div className="space-y-1.5 text-sm">
              <Row label="Sub-total" value={money(subTotal.toFixed(2))} />
              {taxTotal > 0 ? <Row label="GST" value={money(taxTotal.toFixed(2))} /> : null}
              {anyDiscount ? <Row label="Discount" value={`- ${money(discountTotal.toFixed(2))}`} /> : null}
              {Math.abs(roundOff) >= 0.005 ? (
                <Row label="Round-off" value={`${roundOff > 0 ? '+' : ''}${roundOff.toFixed(2)}`} muted />
              ) : null}
            </div>

            <div className="mt-3 flex items-baseline justify-between border-t border-silver-100 pt-3">
              <span className="text-sm font-medium">{docMode === 'ESTIMATE' ? 'Estimated total' : 'Payable'}</span>
              <span className="num text-2xl font-bold">{money(total.toFixed(2))}</span>
            </div>
            {advanceUsed > 0 && docMode === 'BILL' ? (
              <>
                <div className="flex items-baseline justify-between text-sm text-gold-700">
                  <span>From their advance</span>
                  <span className="num">− {money(advanceUsed.toFixed(2))}</span>
                </div>
                <div className="flex items-baseline justify-between text-sm font-semibold">
                  <span>Still to pay</span>
                  <span className="num">{money(stillToPay.toFixed(2))}</span>
                </div>
                <div className="flex items-baseline justify-between text-2xs text-ink-soft">
                  <span>Advance left after this bill</span>
                  <span className="num">{money((onAccount - advanceUsed).toFixed(2))}</span>
                </div>
              </>
            ) : null}

            {showProfit ? (
              <div className="mt-1 flex items-baseline justify-between">
                <span className="text-2xs text-ink-soft">Profit on this bill</span>
                <span className={`num text-sm font-semibold ${billProfit < 0 ? 'text-danger' : 'text-success'}`}>
                  {billProfit < 0 ? '-' : ''}
                  {money(Math.abs(billProfit).toFixed(2))}
                </span>
              </div>
            ) : null}

            <button
              className="btn-primary mt-4 w-full py-2.5 text-base"
              onClick={() => void post()}
              disabled={busy || !activeBranch}
            >
              {busy
                ? docMode === 'ESTIMATE'
                  ? 'Saving…'
                  : 'Posting…'
                : !activeBranch
                  ? 'Choose a branch first'
                  : docMode === 'ESTIMATE'
                    ? `Save estimate · ${money(total.toFixed(2))}`
                    : `Take ${money(stillToPay.toFixed(2))}`}
            </button>
            {customer ? (
              <p className="mt-1 text-center text-2xs text-ink-soft">Billing to {customer.name}</p>
            ) : (
              <p className="mt-1 text-center text-2xs text-ink-faint">Walk-in customer</p>
            )}
          </div>
        </div>
      ) : null}

      {lines.length > 0 ? (
        <details className="mt-6">
          <summary className="cursor-pointer text-sm text-ink-soft">How the last line was worked out</summary>
          <ol className="mt-2 space-y-1 text-xs">
            {lines[lines.length - 1]!.quote.trace.map((step, i) => (
              <li key={i} className="num">
                <span className="text-ink-soft">{step.step}</span> {step.expression} = {step.result}
              </li>
            ))}
          </ol>
        </details>
      ) : null}
    </>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className={muted ? 'text-2xs text-ink-faint' : 'text-ink-soft'}>{label}</span>
      <span className={`num ${muted ? 'text-2xs text-ink-faint' : ''}`}>{value}</span>
    </div>
  );
}
